import { Trophy } from "lucide-react";
import { Panel, Empty } from "./chartTheme";
import { scorecard, PROVIDERS } from "../../lib/scorecard";
import { fmt } from "../../lib/api";
import { useSort, SortTh } from "../../lib/useSort";

const NAT_COLS = [["rank", "#"], ["provider", "Provider"], ["n", "Tests"], ["dl", "Median ↓"], ["ul", "Median ↑"], ["ping", "Median ping"], ["reliability", "Reliability"], ["score", "Score"]];
const NAT_ACC = Object.fromEntries(NAT_COLS.map(([k]) => [k, (r) => r[k]]));
const MAT_ACC = { province: (r) => r.province, best: (r) => r.best, ...Object.fromEntries(PROVIDERS.map((p) => [p, (r) => r.cells[p]?.score])) };

const heat = (s) => (s >= 75 ? "bg-[#32D74B]/20 text-[#32D74B]" : s >= 50 ? "bg-[#FF9F0A]/15 text-[#FF9F0A]" : "bg-[#FF3B30]/15 text-[#FF6B61]");

const National = ({ rows }) => {
  const { sorted, sort, toggle } = useSort(rows.map((r, i) => ({ ...r, rank: i + 1 })), NAT_ACC);
  return (
  <table className="w-full text-xs" data-testid="scorecard-national">
    <thead className="text-neutral-500 text-left">
      <tr>{NAT_COLS.map(([k, l]) => <SortTh key={k} k={k} label={l} sort={sort} toggle={toggle} />)}</tr>
    </thead>
    <tbody>
      {sorted.map((r) => (
        <tr key={r.provider} className="border-t border-white/5" data-testid={`scorecard-rank-${r.provider}`}>
          <td className="py-2 pr-3">{r.rank === 1 ? <Trophy className="w-3.5 h-3.5 text-[#FFD60A]" /> : r.rank}</td>
          <td className="py-2 pr-3 text-white font-medium">{r.provider}</td>
          <td className="pr-3">{r.n}</td>
          <td className="pr-3">{fmt(r.dl)} Mbps</td>
          <td className="pr-3">{fmt(r.ul)} Mbps</td>
          <td className="pr-3">{fmt(r.ping, 0)} ms</td>
          <td className="pr-3">{fmt(r.reliability, 0)}%</td>
          <td className="pr-3"><span className={`px-2 py-0.5 ${heat(r.score)}`}>{r.score}</span></td>
        </tr>
      ))}
    </tbody>
  </table>
  );
};

const Matrix = ({ provinces }) => {
  const { sorted, sort, toggle } = useSort(provinces, MAT_ACC);
  return (
  <div className="overflow-x-auto">
    <table className="w-full text-xs" data-testid="scorecard-matrix">
      <thead className="text-neutral-500 text-left">
        <tr>
          <SortTh k="province" label="Province" sort={sort} toggle={toggle} />
          {PROVIDERS.map((p) => <SortTh key={p} k={p} label={p} sort={sort} toggle={toggle} />)}
          <SortTh k="best" label="Best" sort={sort} toggle={toggle} />
        </tr>
      </thead>
      <tbody>
        {sorted.map(({ province, cells, best }) => (
          <tr key={province} className="border-t border-white/5" data-testid={`scorecard-province-${province}`}>
            <td className="py-2 pr-3 text-white">{province}</td>
            {PROVIDERS.map((p) => {
              const c = cells[p];
              return (
                <td key={p} className="py-2 pr-3">
                  {c ? (
                    <div className={`px-2 py-1 ${heat(c.score)} ${best === p ? "ring-1 ring-[#FFD60A]" : ""}`} title={`${c.n} tests · reliability ${fmt(c.reliability, 0)}% · ping ${fmt(c.ping, 0)}ms`}>
                      <div className="font-medium">{c.score}</div>
                      <div className="text-[10px] opacity-80">{fmt(c.dl)}↓ · {fmt(c.reliability, 0)}% · n{c.n}</div>
                    </div>
                  ) : <span className="text-neutral-700">—</span>}
                </td>
              );
            })}
            <td className="py-2 text-[#FFD60A] font-medium">{best || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

export const ProviderScorecard = ({ tests }) => {
  const { national, provinces } = scorecard(tests);
  return (
    <>
      <Panel title="Provider scorecard: national ranking" className="lg:col-span-3" testId="report-scorecard">
        {national.length ? <National rows={national} /> : <Empty />}
        <div className="text-[10px] text-neutral-600 mt-3">
          Score = 50% speed (median download, capped at 50 Mbps) + 50% reliability (% of tests with no issues). Only Vodacom, MTN, Cell C, Telkom and Rain are ranked.
        </div>
      </Panel>
      <Panel title="Provider scorecard by province" className="lg:col-span-3" testId="report-scorecard-provinces">
        {provinces.length ? <Matrix provinces={provinces} /> : <Empty />}
        <div className="text-[10px] text-neutral-600 mt-3">Each cell: score · median download · reliability · number of tests. Gold outline = best in that province.</div>
      </Panel>
    </>
  );
};
